import { h } from '@omni-torn/ui';
import {
  ATTACK_ALL_TIME,
  attackTotals,
  byOpponent,
  findLogCategory,
  fightsCsvRows,
  fightsIn,
  joinMugs,
  keyMeets,
  mugsCsvRows,
  mugsIn,
  rangeText,
  readMugLine,
  resultRows,
  sumPerDay,
  type DateRange,
  type Mug,
  type MugLine,
  backfillCursor,
  ownEntryTime,
  pageSpan,
  type Span,
} from '@omni-torn/shared';
import { forgetLogText, logKeptWhere, ownHistoryOn, ownRead, ownSend, readingLogText } from '../own-history';
import { firstRead, LogReader, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { readAttacks, type Attack } from '../read';
import { server, session, torn, tornCallsLeft } from '../session';
import { box, ext, facts, gated, loading, money, num, problem, profileUrl, short, table, tabs, tct } from '../ui';

/**
 * Attacks (You → Attacks; website roadmap S30 — torn.report's attacks & mugs report). Your own fights only:
 *  - Torn's latest 100 (`attacks`, Limited key — one call with your personal stats), plus the year background tracking
 *    keeps on the Omni-Torn server for you (`/api/me/attacks`), plus older pages from Torn when you click for them (kept
 *    for this visit only);
 *  - per day made and taken, results in Torn's own words, respect, a row per opponent;
 *  - mugs: who (your fights) and how much (Torn's log lines, Full key — read into this browser by the log kit, from the
 *    "Attacking" category when Torn lists it). A mug without a log amount says so; nothing is guessed.
 * Nothing new is sent to or kept on the server.
 */

const DAY = 86_400_000;
const LOG_NEED = { level: 4, user: ['log'] } as const;
type Show = 'all' | 'out' | 'in';
type Measure = 'inout' | 'won' | 'respect' | 'mugged';
const MEASURES: ReadonlyArray<readonly [Measure, string]> = [
  ['inout', 'Attacks made and on you'],
  ['won', 'Attacks won'],
  ['respect', 'Respect'],
  ['mugged', 'Money mugged'],
];

/** Older fights read from Torn on a click, this visit only (per player). */
const older = new Map<number, Record<string, unknown>>();

const who = (p: Attack['attacker']) => (p.id ? ext(profileUrl(p.id), p.name || `#${p.id}`) : h('span', { class: 'muted' }, p.name || 'Someone (stealthed)'));
const mugWho = (m: Mug) => (m.opponent.id ? ext(profileUrl(m.opponent.id), m.opponent.name || `#${m.opponent.id}`) : h('span', { class: 'muted' }, 'Not named in the log'));
const tile = (label: string, value: string, data = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
const when = (ms: number) => h('span', { title: new Date(ms).toUTCString() }, tct(ms));
/** Money over a number of mugs, some of them without an amount in the log: "$1,000 (2, 1 not known)". */
const mugCell = (sum: number, n: number, unknown: number) => (!n ? '—' : unknown === n ? `not known (${num(n)})` : `${money(sum)} (${num(n)}${unknown ? `, ${num(unknown)} not known` : ''})`);
const fileName = (what: string, r: DateRange) => `${what}-${rangeText(r).replace(/[^\w]+/g, '-').toLowerCase()}.csv`;

export async function renderAttacks(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const fullKey = keyMeets(me.access ?? null, LOG_NEED);
  const q = new URLSearchParams(location.search);
  let range: DateRange = rangeFromAddress('30d');
  let showing: Show = (['all', 'out', 'in'] as const).find((x) => x === q.get('show')) ?? 'all';
  let measure: Measure = MEASURES.find(([m]) => m === q.get('measure'))?.[0] ?? 'inout';
  let resultFilter = q.get('result') ?? '';

  const body = h('div', {}, loading('Reading your fights…'));
  const sub = h('p', { class: 'muted' }, 'Your fights, from Torn.');
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Attacks'), sub), body);

  // ------------------------------------------------------------ your fights (and your all-time counters)

  let live: Record<string, unknown> = {};
  let kept: { attacks: Record<string, unknown>; count: number; since: number | null } | null = null;
  let personal: Record<string, number> | null = null;
  /** Terms 19: the stretches of your fights the server knows complete (nothing there is read from Torn again). */
  let keptSpans: Span[] = [];
  const onServer = ownHistoryOn();
  try {
    // One Torn call: your latest 100 fights and your personal stats; and what the server keeps for you (terms 19: every
    // fight any device, tracking or the backfill read; below: what background tracking kept, a year).
    const [user, k] = await Promise.all([torn('/user/', { selections: 'attacks,personalstats' }), onServer ? keptFights() : server<{ attacks: Record<string, unknown>; count: number; since: number | null }>('GET', '/api/me/attacks?days=365').catch(() => null)]);
    live = ((user as { attacks?: Record<string, unknown> }).attacks ?? {}) as Record<string, unknown>;
    kept = k;
    // This read goes to the server for you (terms 19): from the oldest of these 100 up to now.
    if (onServer) {
      const times = Object.values(live).map((e) => ownEntryTime('attacks', e)).filter((t): t is number => t !== null);
      void ownSend('attacks', live, pageSpan(null, times, 100, Date.now())).then((sp) => void (sp && (keptSpans = sp)));
    }
    const ps = (user as { personalstats?: Record<string, unknown> }).personalstats;
    if (ps && typeof ps === 'object') personal = Object.fromEntries(Object.entries(ps).filter(([, x]) => typeof x === 'number')) as Record<string, number>;
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  /** What the server keeps of your fights (terms 19), newest first, up to 10 000. */
  async function keptFights(): Promise<{ attacks: Record<string, unknown>; count: number; since: number | null } | null> {
    const all: Record<string, unknown> = {};
    let before: number | undefined;
    let count = 0;
    for (let i = 0; i < 5; i++) {
      const r = await ownRead('attacks', { limit: 2000, ...(before ? { before } : {}) }).catch(() => null);
      if (!r?.on) return null;
      keptSpans = r.spans;
      count = r.count;
      Object.assign(all, r.entries);
      const times = Object.values(r.entries).map((e) => ownEntryTime('attacks', e)).filter((t): t is number => t !== null);
      if (!r.more || !times.length) break;
      before = Math.min(...times);
    }
    const times = Object.values(all).map((e) => ownEntryTime('attacks', e)).filter((t): t is number => t !== null);
    return { attacks: all, count, since: times.length ? Math.min(...times) : null };
  }

  let fights: Attack[] = [];
  const merge = () => {
    fights = readAttacks({ attacks: { ...(older.get(pid) ?? {}), ...(kept?.attacks ?? {}), ...live } }, pid);
    const oldest = fights.length ? fights[fights.length - 1]!.started : null;
    if (onServer) sub.textContent = `Your ${num(fights.length)} fights known${oldest ? ` since ${tct(oldest)} TCT` : ''} — kept for you on Omni-Torn (everything any of your devices read, and older fights read while you’re away${backfillCursor(keptSpans) === 0 ? ' — all of them' : ''}), plus Torn’s latest.`;
    else if (kept?.count) sub.textContent = `Your ${num(fights.length)} fights known${oldest ? ` since ${tct(oldest)} TCT` : ''} — kept by background tracking${kept.since ? ` since ${tct(kept.since)} TCT` : ''} (a year), plus Torn’s latest.`;
    else sub.textContent = `Your ${num(fights.length)} fights known${oldest ? ` since ${tct(oldest)} TCT` : ''}, from Torn (its latest 100 at a time). With background tracking on, a year of them is kept for you.`;
  };
  merge();

  // ------------------------------------------------------------ your log: the money of each mug (Full key)

  let store: LogStore | null = null;
  let reader: LogReader | null = null;
  let first: Promise<void> | null = null;
  let mugLines: MugLine[] = [];
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });
  /** "Read older fights" notes (kept across redraws). */
  const olderNote = h('span', { class: 'muted small', 'data-fights-note': '' });

  async function ensureReader(): Promise<LogReader> {
    if (reader) return reader;
    store = await logStore(pid);
    let cat: number | null = null;
    try {
      cat = findLogCategory(await torn('/v2/torn/logcategories', {}, DAY), /^attack(s|ing)?$/i);
    } catch {
      /* the whole log, mugs picked out here */
    }
    const stream: LogStream = cat ? { id: `cat:${cat}`, cat } : { id: 'all' };
    reader = new LogReader(store, stream);
    return reader;
  }

  async function loadMugLines(): Promise<void> {
    if (!store) return;
    mugLines = (await store.range({ from: null, to: null })).map((l) => readMugLine(l)).filter((l): l is MugLine => !!l);
  }

  const keptHolder = h('div', {});
  async function fillKept(): Promise<void> {
    if (!reader || !store) return;
    const mine = mugLines.length;
    keptHolder.replaceChildren(
      await keptLogPanel({
        reader,
        store,
        range,
        matching: mine,
        what: 'mugs',
        note: logNote,
        forgetText: forgetLogText(),
        onForget: () => {
          reader = null;
          store = null;
          first = null;
          mugLines = [];
        },
        redraw: () => void refreshLog(),
      }),
    );
  }

  /** After a read (or "Forget"): the mug lines again, then the page. */
  async function refreshLog(): Promise<void> {
    try {
      await ensureReader();
      await loadMugLines();
      draw();
      await fillKept();
    } catch (err) {
      keptHolder.replaceChildren(problem(err));
    }
  }

  // ------------------------------------------------------------ the page

  const setQ = () => setAddressQuery(range, { show: showing === 'all' ? null : showing, measure: measure === 'inout' ? null : measure, result: resultFilter || null });
  const onRange = (r: DateRange) => {
    range = r;
    setQ();
    draw();
    void fillKept();
  };

  function chartBox(shown: readonly Attack[], mugs: readonly Mug[]): HTMLElement {
    const now = Date.now();
    const out = shown.filter((a) => a.outgoing);
    const at = (a: Attack) => ({ at: a.started });
    const one = (label: string, pts: Array<{ at: number; v: number }>, format?: (n: number) => string) => h('div', { 'data-chart': label }, h('h3', { class: 'small' }, label), zoomChart(pts, { label, onZoom: onRange, ...(format ? { format } : {}) }));
    let charts: HTMLElement[];
    if (measure === 'inout')
      charts = [
        one('Attacks you made', sumPerDay(out.map(at), () => 1, range, now)),
        one('Attacks on you', sumPerDay(shown.filter((a) => !a.outgoing).map(at), () => 1, range, now)),
      ];
    else if (measure === 'won') charts = [one('Attacks won', sumPerDay(out.filter((a) => a.won).map(at), () => 1, range, now))];
    else if (measure === 'respect') charts = [one('Respect', sumPerDay(out.map((a) => ({ at: a.started, v: a.respect })), (x) => x.v, range, now), (n) => num(n, 1))];
    else charts = [one('Money mugged', sumPerDay(mugs.filter((m) => m.dir === 'out' && m.amount !== null).map((m) => ({ at: m.at, v: m.amount! })), (x) => x.v, range, now), (n) => `$${short(n)}`)];
    const pick = filterSelect('Chart', MEASURES, measure, (v) => {
      measure = v as Measure;
      setQ();
      draw();
    }, 'measure');
    return box('Per day', [h('div', { class: charts.length > 1 ? 'cards two' : '' }, ...charts)], { id: 'attacks-chart', extra: pick });
  }

  function mugsBox(mugs: readonly Mug[]): HTMLElement {
    const noAmount = mugs.filter((m) => m.amount === null).length;
    const note = !fullKey
      ? 'The money of each mug is in your log, which needs a Full Access key — without it, who and when only (from your fights).'
      : noAmount
        ? `${num(noAmount)} of these have no amount: the log ${logKeptWhere()} has no line for them (or the line doesn’t say) — “Read older” in the log box below goes further back. Nothing is guessed.`
        : 'Money from your log’s own lines; who and when from your fights.';
    return box(
      `Mugs (${num(mugs.length)})`,
      [
        table(mugs.slice(0, 500), [
          { label: 'When', get: (m) => when(m.at), sort: (m) => m.at },
          { label: 'Who', get: (m) => (m.dir === 'out' ? 'You mugged' : 'Mugged you'), sort: (m) => m.dir },
          { label: 'Player', get: mugWho, sort: (m) => m.opponent.name || String(m.opponent.id ?? '') },
          { label: 'Faction', get: (m) => m.opponent.faction || '—', sort: (m) => m.opponent.faction },
          { label: 'Money', get: (m) => (m.amount === null ? h('span', { class: 'muted', title: 'Not in your log kept here' }, '—') : h('span', { class: m.dir === 'out' ? 't-green' : 't-red' }, money(m.amount))), sort: (m) => m.amount ?? -1, cls: 'r' },
        ], { csv: false, initial: 0, empty: 'No mugs in this range.' }),
        mugs.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(mugs.length)} — the CSV has them all.`) : null,
        h('p', { class: 'muted small', 'data-mugs-note': '' }, note),
      ].filter(Boolean) as HTMLElement[],
      { id: 'mugs', extra: csvButton(() => fileName('mugs', range), () => mugsCsvRows(mugs)) },
    );
  }

  function fightsBox(shown: readonly Attack[], mugs: readonly Mug[]): HTMLElement {
    const out = shown.filter((a) => a.outgoing);
    const inc = shown.filter((a) => !a.outgoing);
    const results = [...new Set(shown.map((a) => a.result).filter(Boolean))].sort();
    const base = showing === 'out' ? out : showing === 'in' ? inc : shown;
    const list = resultFilter ? base.filter((a) => a.result.toLowerCase() === resultFilter.toLowerCase()) : base;
    const mugMoney = new Map(mugs.filter((m) => m.fightId).map((m) => [m.fightId!, m.amount]));
    const olderBtn = h('button', { type: 'button', class: 'btn-small', 'data-action': 'fights-older' }, 'Read older fights from Torn') as HTMLButtonElement;
    olderBtn.addEventListener('click', async () => {
      olderBtn.disabled = true;
      olderNote.textContent = ' Reading…';
      try {
        olderNote.textContent = await readOlderFights();
        merge();
        draw();
      } catch (err) {
        olderNote.replaceChildren(problem(err));
        olderBtn.disabled = false;
      }
    });
    return box(
      'Fights',
      [
        h(
          'div',
          { class: 'toolbar' },
          tabs(
            [
              { id: 'all', label: `All (${num(shown.length)})` },
              { id: 'out', label: `Yours (${num(out.length)})` },
              { id: 'in', label: `On you (${num(inc.length)})` },
            ],
            showing,
            (id) => {
              showing = id as Show;
              setQ();
              draw();
            },
          ),
          filterSelect('Result', [['', 'Every result'], ...results.map((r) => [r, r] as const)], resultFilter, (v) => {
            resultFilter = v;
            setQ();
            draw();
          }, 'result'),
        ),
        table(list.slice(0, 500), [
          { label: 'When', get: (a) => when(a.started), sort: (a) => a.started },
          { label: 'Attacker', get: (a) => who(a.attacker), sort: (a) => a.attacker.name },
          { label: 'Defender', get: (a) => who(a.defender), sort: (a) => a.defender.name },
          { label: 'Result', get: (a) => h('span', { class: a.won ? 't-green' : 't-red' }, a.result), sort: (a) => a.result },
          { label: 'Respect', get: (a) => (a.respect ? num(a.respect, 2) : '—'), sort: (a) => a.respect, cls: 'r' },
          { label: 'FF', get: (a) => (a.fairFight ? num(a.fairFight, 2) : '—'), sort: (a) => a.fairFight ?? 0, cls: 'r' },
          { label: 'Chain', get: (a) => (a.chain ? String(a.chain) : '—'), sort: (a) => a.chain, cls: 'r' },
          { label: 'Mugged', get: (a) => { const m = mugMoney.get(a.id); return m === undefined ? '' : m === null ? h('span', { class: 'muted', title: 'Not in your log kept here' }, '—') : money(m); }, sort: (a) => mugMoney.get(a.id) ?? -1, cls: 'r' },
        ], { csv: false, initial: 0, empty: 'No fights in this list.' }),
        list.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(list.length)} — the CSV has them all.`) : null,
        h('div', { class: 'toolbar' }, olderBtn, olderNote),
        h('p', { class: 'muted small' }, 'Older fights come from Torn 100 at a time on your click (within this site’s share of your calls) and are kept only while this page is open.'),
      ].filter(Boolean) as HTMLElement[],
      { id: 'fights', extra: csvButton(() => fileName('attacks', range), () => fightsCsvRows(list, mugs)) },
    );
  }

  function allTimeBox(): HTMLElement | null {
    if (!personal) return null;
    const rows = ATTACK_ALL_TIME.filter(([k]) => personal![k] !== undefined).map(([k, label, kind]) => [label, kind === '$' ? money(personal![k]!) : num(personal![k]!)] as [string, string]);
    return rows.length ? box('All time (Torn’s own counters)', facts(rows), { id: 'attacks-all-time' }) : null;
  }

  function draw(): void {
    const shown = fightsIn(fights, range);
    const mugs = mugsIn(joinMugs(fights, mugLines), range);
    const t = attackTotals(shown, mugs);
    const mugTile = (s: typeof t.mugged) => {
      const known = s.count - s.unknown;
      if (!s.count) return '$0';
      return known ? `${money(s.money)}${s.unknown ? ` + ${num(s.unknown)} not known` : ''}` : `${num(s.unknown)} not known`;
    };
    const opponents = byOpponent(shown, mugs);
    body.replaceChildren(
      ...([
        rangePicker(range, onRange),
        h(
          'div',
          { class: 'tiles' },
          tile('Attacks won', `${num(t.won)} / ${num(t.made)}`, 'won'),
          tile('Defends held', `${num(t.held)} / ${num(t.taken)}`, 'held'),
          tile('Respect earned', num(t.respect, 2), 'respect'),
          tile('Average fair fight', t.fairFight === null ? '—' : num(t.fairFight, 2), 'ff'),
          tile(`Money mugged (${num(t.mugged.count)})`, mugTile(t.mugged), 'mugged'),
          tile(`Mugged you (${num(t.muggedYou.count)})`, mugTile(t.muggedYou), 'mugged-you'),
        ),
        chartBox(shown, mugs),
        h(
          'div',
          { class: 'cards two' },
          box(
            'Results',
            table(resultRows(shown), [
              { label: 'Result', get: (r) => r.result, sort: (r) => r.result },
              { label: 'Yours', get: (r) => num(r.out), sort: (r) => r.out, cls: 'r' },
              { label: 'On you', get: (r) => num(r.in), sort: (r) => r.in, cls: 'r' },
            ], { initial: 1, empty: 'No fights in this range.' }),
            { id: 'attack-results' },
          ),
          box(
            'Respect',
            facts([
              ['Earned', num(t.respect, 2)],
              ['Per hit that earned any', t.respectPerHit === null ? '—' : num(t.respectPerHit, 2)],
              ['Best hit', t.bestRespect === null ? '—' : num(t.bestRespect, 2)],
              ['Largest mug', t.mugged.largest === null ? '—' : money(t.mugged.largest)],
            ]),
            { id: 'attack-respect' },
          ),
        ),
        mugsBox(mugs),
        box(
          `By opponent (${num(opponents.length)})`,
          [
            table(opponents, [
              { label: 'Player', get: (r) => ext(profileUrl(r.id), r.name || `#${r.id}`), sort: (r) => r.name },
              { label: 'Faction', get: (r) => r.faction || '—', sort: (r) => r.faction },
              { label: 'Your hits', get: (r) => (r.hits ? `${num(r.won)} / ${num(r.hits)}` : '—'), sort: (r) => r.hits, cls: 'r' },
              { label: 'Respect', get: (r) => (r.respect ? num(r.respect, 2) : '—'), sort: (r) => r.respect, cls: 'r' },
              { label: 'You mugged', get: (r) => mugCell(r.mugged, r.mugs, r.mugsUnknown), sort: (r) => r.mugged, cls: 'r' },
              { label: 'Their hits', get: (r) => (r.theirs ? `${num(r.held)} held / ${num(r.theirs)}` : '—'), sort: (r) => r.theirs, cls: 'r' },
              { label: 'They mugged', get: (r) => mugCell(r.lost, r.muggedYou, r.muggedYouUnknown), sort: (r) => r.lost, cls: 'r' },
              { label: 'Last', get: (r) => when(r.last), sort: (r) => r.last },
            ], { initial: 2, empty: 'Nobody in this range.' }),
            h('p', { class: 'muted small' }, 'Stealthed attackers aren’t named by Torn, so they aren’t here. Mug money counts the amounts your log gives.'),
          ],
          { id: 'by-opponent' },
        ),
        fightsBox(shown, mugs),
        allTimeBox(),
        fullKey ? keptHolder : gated('Mug money from your log', LOG_NEED, 'your log gives the money of each mug.', () => h('div', {})),
      ].filter(Boolean) as HTMLElement[]),
    );
  }

  /** Torn's fights before the oldest known, 100 a call, up to 5 calls (leaving this minute's share for other pages). */
  async function readOlderFights(): Promise<string> {
    const mine = older.get(pid) ?? {};
    older.set(pid, mine);
    let added = 0;
    for (let pages = 0; pages < 5; pages++) {
      // The server already has every fight (terms 19): nothing to ask Torn.
      if (onServer && backfillCursor(keptSpans) === 0) return `${num(added)} older — that’s everything Torn has.`;
      if (tornCallsLeft() <= 3) return `${num(added)} older — this minute’s share of your Torn calls is used; carry on in a minute.`;
      const oldest = Math.min(...fights.map((a) => a.started), Date.now());
      const got = ((await torn('/user/', { selections: 'attacks', to: String(Math.floor(oldest / 1000) - 1) }, 600_000)) as { attacks?: Record<string, unknown> }).attacks ?? {};
      if (onServer) {
        const times = Object.values(got).map((e) => ownEntryTime('attacks', e)).filter((t): t is number => t !== null);
        const sp = await ownSend('attacks', got, pageSpan(Math.floor(oldest / 1000) * 1000, times, 100, Date.now()));
        if (sp) keptSpans = sp;
      }
      const known = new Set(fights.map((a) => a.id));
      let fresh = 0;
      for (const [id, raw] of Object.entries(got)) {
        const code = (raw as { code?: unknown })?.code;
        if (known.has(typeof code === 'string' && code ? code : id)) continue;
        mine[id] = raw;
        fresh++;
      }
      added += fresh;
      merge();
      if (!fresh) return `${num(added)} older — that’s everything Torn has.`;
    }
    return `${num(added)} older — there’s more.`;
  }

  draw();

  // Then the log (Full key): what the server kept and Torn's newest pages of the Attacking category, into this browser.
  if (fullKey) {
    keptHolder.replaceChildren(loading(readingLogText()));
    try {
      const r = await ensureReader();
      first ??= firstRead(r).then(
        (t) => void (logNote.textContent = t),
        (err: unknown) => void logNote.replaceChildren(problem(err)),
      );
      await first;
      if (!view.contains(body)) return;
      await refreshLog();
    } catch (err) {
      keptHolder.replaceChildren(problem(err));
    }
  }
}
